/* eslint-disable react-hooks/exhaustive-deps */
// cspell: ignore geocode
import { PaginatedResponse } from '@eagle/api-types';
import { Time } from '@eagle/common';
import { PersonThingEvent, ThingEventSnapshot } from '@eagle/core-data-types';
import { FaultEventTypes, FaultUpdate, FeatureTypes, TimeZone, TrackingEventTypes } from '@eagle/data-function-types';
import { Accordion, AccordionDetails, AccordionSummary, Collapse, Dialog, IconButton, Stack, styled, SwipeableDrawer, Switch, Tooltip, Typography } from '@ethings/ui-toolkit';
import { Box, FormControlLabel, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import { debounce, omit } from 'lodash';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { THING_EVENT_API_LIMIT } from '../../constants';
import { FAULTS_VIEW_FLAG } from '../../feature-flags';
import { usePromise, useSmallScreen } from '../../hooks';
import { useEventSummarization } from '../../hooks/use-event-summarization';
import { useRecursive } from '../../hooks/use-recursive';
import { EventFields } from '../../pages/event-history/history-drawer/history-drawer.types';
import { useSearch } from '../../pages/list/use-search';
import { centered, scrollbar } from '../../style';
import { CacheDataTypes, Maybe, Nullable, Undefinable } from '../../types';
import { formatDateRange, formatTimeRange, getEventFeature, getEventFeatureV2, getEventLabel, getEventLabelV2, getFeatureInstance, isMatchingDay, validateLocationType } from '../../util';
import { DateTimeRangePickerProvider } from '../../util/data-time-range-picker';
import { ProcessedEventType, SummarizationMode } from '../../util/event-summarization';
import CircularProgressIndicator from '../circular-progress-indicator/circular-progress-indicator';
import { AppliedFilter, EntityField } from '../entity-search';
import { ErrorMessage } from '../error-message';
import { useEventDialog } from '../event-dialog';
import { EventCategories } from '../events-table';
import { useBoolFlag, useFlags, useNumberFlag } from '../flags';
import { FormatDate } from '../format';
import { Icon } from '../icons';
import { InlineDateTimeRangePicker } from '../inline-date-time-range-picker/inline-date-time-range-picker';
import { ClusterTabularData } from '../map/layers/event-cluster/cluster-tabular-data';
import { AnyEventData, EventLocationData, isSummarizedEvent } from '../map/thing-event-pane/thing-event-pane.types';
import { TimeLine, TimelineData } from '../timeline';
import { useBatchFaultDataSafe } from './batch-fault-context';
import { EntityCardActions } from './entity-card-actions';
import { CardAction, JourneyItem } from './entity-journey-types';
import { JourneyFaultsDialog } from './journey-faults-dialog';
import { NormalizedFaultEventItem } from './journey-faults.utils';
import { useNotableEventsContext } from './notable-events-context';
import { useHistorySearch } from './use-history-search';
import { getEventDictionary, getJourneyColor, isValidEvent } from './util';

interface Props {
  collapsible?: boolean;
  data: JourneyItem;
  'data-testid'?: string;
  entityDisplay: string;
  fetchEventEndTime: Date;
  fetchEventStartTime: Date;
  hoveredEventId: Maybe<string>;
  selectedEvent: Maybe<EventLocationData>;
  setHoveredEventId: Dispatch<SetStateAction<Maybe<string>>>;
  setJourneyEvents: Dispatch<SetStateAction<Record<string, Undefinable<ThingEventSnapshot[]>>>>;
  setSelectedEvent: Dispatch<SetStateAction<Maybe<EventLocationData>>>;
  setShowLocationUpdateEvents?: Dispatch<SetStateAction<Record<string, boolean>>>;
}

interface StyledFormControlLabelProps {
  isLoading: boolean;
}

const StyledFormControlLabel = styled(FormControlLabel)<StyledFormControlLabelProps>(({ isLoading }) => ({
  opacity: isLoading ? 0.7 : 1,
}));

const StyledIconButton = styled(IconButton)({
  padding: 0,
});

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  '&.MuiDrawer-root > .MuiPaper-root': {
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': { minHeight: 'fit-content', padding: 0 },
  '& .MuiAccordionSummary-root.Mui-expanded': { minHeight: 'fit-content' },
  '& .MuiAccordionDetails-root': { padding: `${theme.spacing(1)} 0` },
  boxShadow: 'none',
}));

const StyledAccordionSummary = styled(AccordionSummary)({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-content': {
    justifyContent: 'space-between',
    margin: 0,
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: 0,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(180deg)',
  },
});

type EventFiltersType = typeof EventCategories[keyof typeof EventCategories] | unknown[];

export const HISTORY_DEFAULT_MAX_DATE_RANGE = 24;
export const HISTORY_DEFAULT_DATE_RANGE_UNIT = 'hours';
export const HISTORY_DATE_RANGE_FLAG = 'track-thing-history-custom-date-range-feature';
const HISTORY_REQUEST_RETRY_COUNT = 2;

const debouncedDateRangeExtendedToast = debounce((cb: () => void) => {
  cb();
}, 500);

export const JourneyInstance: FC<Props> = ({
  collapsible = true,
  data,
  entityDisplay,
  fetchEventEndTime,
  fetchEventStartTime,
  hoveredEventId,
  selectedEvent,
  setHoveredEventId,
  setJourneyEvents,
  setSelectedEvent,
  setShowLocationUpdateEvents,
  ...props
}) => {
  const {
    dateEditingJourney,
    defaultSelectedEventId,
    entityVisibilities,
    expandedEntity,
    handleExpand,
    mapLoading,
    removeJourney,
    setMapLoading,
    setNextZoomTarget,
    toggleEditingDate,
    toggleVisibleItem,
    updateDateTime,
    updateJourney,
  } = useHistorySearch();
  const initialSelectedEventIdRef = useRef(defaultSelectedEventId ?? null);
  const { restClient } = useAuthenticated();
  const { enqueueSnackbar } = useSnackbar();
  const { filters } = useSearch();
  const { notableEvents } = useNotableEventsContext();
  const { t } = useTranslation(['common', 'track']);
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const smallScreen = useSmallScreen();
  const actionsOpen = Boolean(anchorEl);
  const theme = useTheme();
  const color = getJourneyColor(data.colorIndex, theme);
  const entityVisibility = entityVisibilities.find((visibility) => visibility.entityId === data.entityId);
  const instanceExpanded = (data.entityId === expandedEntity?.entityId) && (data.id === expandedEntity.journeyId);
  const isEditingDate = (dateEditingJourney?.entityId === data.entityId) && (dateEditingJourney.journeyId === data.id);
  const isJourneyVisible = entityVisibility?.journeyVisibility.find((journey) => journey.journeyId === data.id)?.isVisible;
  const loadingHint = t('common:common.labels.loading');
  const darklyTime = useNumberFlag('track-history-feature-notable-event-time-period');
  const timeLineRef = useRef<HTMLDivElement | null>(null);
  const { setEvents, setEventIndex, setOpen, open } = useEventDialog();
  const batchContext = useBatchFaultDataSafe();
  const flags = useFlags();
  const maxDateRange = flags[HISTORY_DATE_RANGE_FLAG] as number || HISTORY_DEFAULT_MAX_DATE_RANGE;
  const isBatchingEnabled = useBoolFlag('track-thing-history-parallel-processing-api-feature');
  const batchSize = useNumberFlag('track-thing-history-parallel-processing-api-batch-settings-feature');
  const timeoutToDisplayToast = useNumberFlag('track-thing-history-parallel-processing-api-first-api-response-time-toast-feature');
  const isRetryingEnabled = useBoolFlag('track-thing-history-retry-api-calls-feature');
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const flagShowLocationSwitchFeature = useBoolFlag('track-thing-history-switch-show-location-updates-in-journey-instance-card-feature');
  const flagShowLocationUpdateEvents = useBoolFlag('track-thing-history-journey-card-show-location-update-events');
  const flagShowLocationSwitch = flagShowLocationUpdateEvents && flagShowLocationSwitchFeature;
  const flagShowNoLocationUpdateEvents = useBoolFlag('track-thing-history-journey-card-show-no-location-update-events');
  const flagShowInaccurateLocationUpdateEvents = useBoolFlag('track-thing-history-journey-card-show-inaccurate-location-update-events');
  const flagFaultsView = useBoolFlag(FAULTS_VIEW_FLAG);
  const locationUpdatesEnabledStorageKey = 'track-thing-history-location-updates-enabled';
  const readStoredLocationUpdatesEnabled = (): boolean => {
    try {
      return window.localStorage.getItem(locationUpdatesEnabledStorageKey) === 'true';
    } catch {
      return false;
    }
  };

  const [locationState, setLocationState] = useState(() => {
    const storedEnabled = readStoredLocationUpdatesEnabled();
    return {
      locationUpdatesEnabled: storedEnabled,
      showLocationSwitchEnabled: storedEnabled,
      isLoading: false,
    };
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(locationUpdatesEnabledStorageKey, String(locationState.showLocationSwitchEnabled));
    } catch {
      // ignore
    }
  }, [locationState.showLocationSwitchEnabled, locationUpdatesEnabledStorageKey]);
  const showLocationEvents = flagShowLocationSwitch && flagShowLocationUpdateEvents && locationState.locationUpdatesEnabled;
  const mapClusteringV2 = useBoolFlag('track-journey-map-cluster-enhancements-temporary');
  const eventSummarizationFeature = useBoolFlag('track-thing-history-event-summarisation-feature');
  const shouldUseSummarizedEvents = mapClusteringV2 && eventSummarizationFeature;

  const [summarizedEventsDialogOpen, setSummarizedEventsDialogOpen] = useState(false);
  const [summarizedEventsForDialog, setSummarizedEventsForDialog] = useState<EventLocationData[]>([]);
  const [selectedFaultItem, setSelectedFaultItem] = useState<Nullable<NormalizedFaultEventItem>>(null);

  const convertToEventLocationData = (event: ThingEventSnapshot): AnyEventData => {
    return {
      ...event,
      data: {
        ...event.data,
        location: event.data.location || { latitude: 0, longitude: 0, source: 'unknown' },
        velocity: event.data.velocity || undefined,
      },
    } as EventLocationData;
  };

  const handleSummarizedEventClick = useCallback((originalEvents: ThingEventSnapshot[], summarizedEvent?: ThingEventSnapshot) => {
    if (!originalEvents?.length) return;

    const representativeEvent = summarizedEvent || originalEvents[0];
    const isFaultDtc = flagFaultsView && representativeEvent.feature === 'fault/dtc' && representativeEvent.eventTypeId === FaultEventTypes.UPDATE;

    if (isFaultDtc) {
      const sortedEvents = [...originalEvents].sort((a, b) => b.occurred.getTime() - a.occurred.getTime());
      const lastOccurredEvent = sortedEvents[0];
      const firstOccurredEvent = sortedEvents[sortedEvents.length - 1];

      setSelectedFaultItem({
        event: omit(representativeEvent, '_isSummarized', '_originalEvents', '_eventCount', '_summarizationMode'),
        originalEvents: sortedEvents,
        firstEventOccurred: firstOccurredEvent.occurred,
        lastEventOccurred: lastOccurredEvent.occurred,
        isOngoing: !!(lastOccurredEvent.data as unknown as FaultUpdate).active,
      });

      setSummarizedEventsDialogOpen(false);
    } else {
      setSummarizedEventsForDialog(originalEvents.map(convertToEventLocationData));
      setSelectedFaultItem(null);
      setSummarizedEventsDialogOpen(true);
    }
  }, [flagFaultsView]);

  const handleCloseAnyDialog = useCallback(() => {
    setSummarizedEventsDialogOpen(false);
    setSummarizedEventsForDialog([]);
    setSelectedFaultItem(null);
  }, []);

  const hasNotableEvent = useRef(false);
  const hasLocationUpdate = useRef(false);
  const startTime = fetchEventStartTime;
  const endTime = fetchEventEndTime;

  const hasExpandedDateRange = useMemo(() => {
    return fetchEventStartTime.getTime() !== data.dateRange.startTime.getTime()
      || fetchEventEndTime.getTime() !== data.dateRange.endTime.getTime();
  }, [data.dateRange.endTime, data.dateRange.startTime, fetchEventEndTime, fetchEventStartTime]);

  const startDateTime = DateTime.fromJSDate(startTime);
  const endDateTime = DateTime.fromJSDate(endTime);

  useEffect(() => {
    if (!setShowLocationUpdateEvents) return;
    setShowLocationUpdateEvents((prev) => ({ ...prev, [data.id]: showLocationEvents || false }));
  }, [showLocationEvents, data.id]);

  const eventFilters = filters.flatMap((filter): EventFiltersType => {
    switch ((filter as AppliedFilter<EntityField>).value.id) {
      case EventFields.DRIVER_BEHAVIOR:
        return EventCategories[EventFields.DRIVER_BEHAVIOR];
      case EventFields.MEDIA:
        return EventCategories.media;
      case EventFields.SAFETY_INCIDENT:
        return EventCategories[EventFields.SAFETY_INCIDENT];
      case EventFields.OTHER:
        return EventCategories.other;
    }
    return [];
  });

  const url = data.entityType === CacheDataTypes.THING_TYPE
    ? `/api/v2/thing-event/thing/${data.entityId}`
    : `/api/v2/person-thing-event/person/${data.entityId}`;

  const locationFilterTypes: TrackingEventTypes[] = [];

  if (!flagShowLocationUpdateEvents) {
    locationFilterTypes.push(TrackingEventTypes.LOCATION_UPDATE);
  }
  if (!flagShowNoLocationUpdateEvents) {
    locationFilterTypes.push(TrackingEventTypes.NO_LOCATION_UPDATE);
  }
  if (!flagShowInaccurateLocationUpdateEvents) {
    locationFilterTypes.push(TrackingEventTypes.INACCURATE_LOCATION_UPDATE);
  }

  const locationFilters = { '$nin': locationFilterTypes };

  const params = {
    dateRangeStart: startTime,
    dateRangeFinish: endTime,
    filter: eventFilters.length ? { 'eventTypeId': { '$in': eventFilters, ...locationFilters } } : { 'eventTypeId': locationFilters },
    limit: THING_EVENT_API_LIMIT,
    sort: { occurred: 1 },
  };

  const transformData = (response: AxiosResponse<PaginatedResponse<ThingEventSnapshot>, any>): AxiosResponse<PaginatedResponse<ThingEventSnapshot>> => {
    if (data.entityType === CacheDataTypes.THING_TYPE) return response;
    const personData = response.data.items as unknown[] as PersonThingEvent[];
    return { ...response, data: { ...response.data, items: personData.map(({ thingEvent }) => thingEvent) } };
  };

  const [thingEventSnapShots, thingEventSnapShotsError, thingEventSnapShotsStatus] = useRecursive<ThingEventSnapshot>(
    url,
    params,
    transformData,
    [data, filters],
    isBatchingEnabled ? batchSize : undefined,
    isRetryingEnabled ? HISTORY_REQUEST_RETRY_COUNT : undefined,
  );

  useDisplaySnackbarAfterTimeout({
    enabled: isBatchingEnabled,
    isPending: thingEventSnapShotsStatus === 'pending',
    message: t('track:page.history.long-api-response.hint.info'),
    timeoutMs: Time.seconds(timeoutToDisplayToast ?? 0),
  });

  const processedEvents = useEventSummarization(thingEventSnapShots?.items || [], notableEvents);

  const processedEventsData = useMemo(() => {
    if (thingEventSnapShotsStatus !== 'resolved' || !thingEventSnapShots) return;

    if (!shouldUseSummarizedEvents) return thingEventSnapShots;

    const processedItems = processedEvents.map((ev) => {
      if (ev.type === ProcessedEventType.Individual) return ev.event;
      return {
        ...ev.representativeEvent,
        _isSummarized: true,
        _originalEvents: ev.originalEvents,
        _eventCount: ev.eventCount,
        _summarizationMode: ev.summarizationMode,
      };
    }).sort((a, b) => new Date(a.occurred).getTime() - new Date(b.occurred).getTime());

    return {
      ...thingEventSnapShots,
      items: processedItems,
      count: processedItems.length,
    };
  }, [thingEventSnapShotsStatus, thingEventSnapShots, shouldUseSummarizedEvents]);

  const [timeZoneFallBack] = usePromise<Undefinable<TimeZone>>(async () => {
    const eventWithLocation = processedEventsData?.items.find(({ data: { location } }) => location);
    const validLocation = validateLocationType(() => eventWithLocation?.data.location);
    if (!processedEventsData || thingEventSnapShotsStatus !== 'resolved' || !eventWithLocation || !validLocation) return;
    const timeZone = await restClient.timeZone.get({
      lat: validLocation.latitude,
      long: validLocation.longitude,
      timestamp: eventWithLocation.occurred,
    });
    return { id: timeZone.id };
  }, [restClient, processedEventsData, thingEventSnapShotsStatus]);

  const timeLineData = useMemo(() => {
    hasNotableEvent.current = false;
    hasLocationUpdate.current = false;
    if (thingEventSnapShotsStatus !== 'resolved' || !processedEventsData?.items.length) {
      return [];
    }

    return processedEventsData?.items.map((item) => {
      const event = item as AnyEventData;
      const { feature, featureTypeId, eventId, eventTypeId, occurred, data: { location }, ...rest } = event;
      const isSummarized = isSummarizedEvent(event);
      const eventData = getEventDictionary({ feature, featureTypeId, eventId, eventTypeId, occurred, data: { location }, ...rest }, theme, notableEvents);
      if (!eventData) return;
      if (eventData.eventTypeId !== TrackingEventTypes.LOCATION_UPDATE && eventData.eventTypeId !== TrackingEventTypes.INACCURATE_LOCATION_UPDATE && eventData.eventTypeId !== TrackingEventTypes.NO_LOCATION_UPDATE) hasNotableEvent.current = true;
      else hasLocationUpdate.current = true;
      if (!isValidEvent(eventData, showLocationEvents)) return;

      const eventLocation = validateLocationType(() => location);
      const featureLabel = flagUseEventV3 ? getEventFeature(feature) : getEventFeatureV2(feature);
      const eventTypeLabel = flagUseEventV3 ? getEventLabel(feature, eventTypeId) : getEventLabelV2(featureTypeId, eventTypeId);
      const secondaryChip = getFeatureInstance(feature);
      const primary = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? featureLabel : eventTypeLabel;
      const secondary = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? eventTypeLabel : featureLabel;

      return {
        color: eventData.styling.backgroundColor,
        date: occurred,
        eventId,
        primary,
        secondary,
        secondaryChip,
        timezone: eventLocation?.timeZone ?? timeZoneFallBack,
        isSummarized,
        eventCount: isSummarized ? event._eventCount : 0,
        originalEvents: isSummarized ? event._originalEvents : [],
        summarizationMode: isSummarized ? event._summarizationMode : SummarizationMode.All,
        summarizedEvent: isSummarized ? event : undefined,
      };
    })
      .filter(Boolean) as Undefinable<TimelineData[]>;
  }, [locationState.locationUpdatesEnabled, thingEventSnapShotsStatus, processedEventsData, timeZoneFallBack, theme, t]);

  const events = useMemo(() => (
    processedEventsData?.items.filter((eventSnapshot) => {
      const eventData = getEventDictionary(eventSnapshot, theme, notableEvents);
      return eventData && isValidEvent(eventData, showLocationEvents);
    })
  ), [locationState.locationUpdatesEnabled, processedEventsData]);

  useEffect(() => {
    setJourneyEvents((prev) => ({ ...prev, [data.id]: events }));
  }, [data.id, events]);

  const thingEventSnapShotsRetrieved = processedEventsData?.items?.length ?? 0;
  const thingEventSnapShotsTotalCount = processedEventsData?.count ?? 0;

  const journeyActions: CardAction[] = [
    {
      name: 'add',
      icon: <Icon icon="Plus" data-testid="plus-icon" />,
      isConfirm: false,
      label: t('common:component.entity-journey.action.change-date-time'),
      onClick: () => {
        setAnchorEl(null);
        toggleEditingDate(data.id, data.entityId);
      },
    }, {
      name: 'delete',
      confirmPrompt: t('common:common.hint.confirm'),
      icon: <Icon icon="Trash" data-testid="trash-icon" />,
      isConfirm: true,
      label: t('common:common.action.remove'),
      onClick: () => {
        setAnchorEl(null);
        removeJourney(data.entityId, data.id);
      },
    },
  ];

  const handleCloseActions = (): void => setAnchorEl(null);

  const handleDateRangeChange = (startDate: Date, endDate: Date): void => {
    updateDateTime(data.entityId, data.id, startDate, endDate);
  };

  const handleClickActions = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleVisibility = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    toggleVisibleItem(data.id, data.entityId);
  };

  const handleZoomTo = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setNextZoomTarget(data.id);
  };

  const debouncedSetHoveredEventId = useCallback(debounce((eventId: Maybe<string>) => {
    setHoveredEventId(eventId);
  }, 200), []);

  const onMouseLeave = useCallback(() => { debouncedSetHoveredEventId(null); }, [debouncedSetHoveredEventId]);

  const onItemClick = useCallback((index: number) => {
    setEventIndex(index);
    setEvents(events ?? []);
    setOpen(true);
  }, [events, setEventIndex, setEvents, setOpen]);

  useEffect(() => {
    const initialSelectedEventId = initialSelectedEventIdRef.current;
    if (!initialSelectedEventId || !events || open) return;

    const eventIndex = events.findIndex((event) => event.eventId === initialSelectedEventId);
    if (eventIndex !== -1) {
      onItemClick(eventIndex);
      handleExpand(data.entityId, data.id);
    } else {
      const foundEvent = processedEventsData?.items?.find((eventSnapshot) => eventSnapshot.eventId === initialSelectedEventId);
      if (foundEvent) {
        setEventIndex(0);
        setEvents([foundEvent]);
        setOpen(true);
      }
    }
    initialSelectedEventIdRef.current = null;
  }, [data.entityId, data.id, onItemClick, open, processedEventsData, setEventIndex, setEvents, setOpen, batchContext]);

  useEffect(() => {
    if (timeLineRef.current || !timeLineData?.length) {
      setLocationState((prevState) => ({ ...prevState, isLoading: false }));
    }
  }, [timeLineData]);

  const handleLocationToggle = useCallback((): void => {
    setLocationState((prevState) => ({
      ...prevState,
      showLocationSwitchEnabled: !prevState.showLocationSwitchEnabled,
      isLoading: true,
    }));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLocationState((prevState) => ({ ...prevState, locationUpdatesEnabled: locationState.showLocationSwitchEnabled })), 50);
    return () => clearTimeout(timer);
  }, [locationState.showLocationSwitchEnabled]);

  const renderAccordionTitle = (): JSX.Element => {
    if (isEditingDate) return <Typography fontStyle="italic">{t('common:component.entity-journey.action.set-date-range')}</Typography>;

    return <>
      <Typography color="text.secondary" fontSize="small">
        {!isMatchingDay(startDateTime, endDateTime) && (endTime > startTime)
          ? formatDateRange(startDateTime, endDateTime)
          : <FormatDate value={startTime} />
        }
      </Typography>
      <Typography>
        {formatTimeRange(startTime, endTime)}
      </Typography>
    </>;
  };

  const renderAccordionDetails: JSX.Element = useMemo(() => {
    if (!timeLineData?.length) {
      if (hasLocationUpdate.current) {
        return (
          <Typography fontStyle="italic">
            {t('common:common.labels.no-notable-events-show-location-updates')}
          </Typography>
        );
      }
      return (
        <Typography data-testid="no-events-label" fontStyle="italic">
          {t('common:common.labels.no-notable-events-or-location-updates-found')}
        </Typography>
      );
    }

    return (
      <Box ref={timeLineRef} sx={{ ...scrollbar, maxHeight: 500, overflow: 'auto', opacity: locationState.isLoading ? 0.5 : 1 }}>
        <TimeLine
          data-testid="timeline"
          data={timeLineData}
          hoveredEventId={hoveredEventId}
          minTimeDifference={{ minutes: darklyTime ?? 5 }}
          onClick={onItemClick}
          onMouseEnter={debouncedSetHoveredEventId}
          onMouseLeave={onMouseLeave}
          onSummarizedEventClick={handleSummarizedEventClick}
          startTime={startTime}
        />
      </Box>
    );
  }, [data, hoveredEventId, instanceExpanded, isEditingDate, timeLineData, locationState.isLoading]);

  useEffect(() => {
    setMapLoading((previousLoading) => ({ ...previousLoading, [data.id]: thingEventSnapShotsStatus === 'pending' }));
  }, [thingEventSnapShotsStatus]);

  useEffect(() => {
    if (!processedEventsData || thingEventSnapShotsStatus !== 'resolved') return;
    updateJourney(
      data,
      processedEventsData as PaginatedResponse<EventLocationData>,
      true,
      entityDisplay,
    );
  }, [processedEventsData]);

  useEffect(() => {
    if (!instanceExpanded || open) return;
    setEvents(events ?? []);
  }, [events]);

  useEffect(() => {
    if (thingEventSnapShotsError) {
      enqueueSnackbar(<ErrorMessage error={thingEventSnapShotsError} />, { variant: 'error' });
    }
  }, [thingEventSnapShotsError]);

  useEffect(() => {
    if (hasExpandedDateRange) {
      debouncedDateRangeExtendedToast(() => {
        enqueueSnackbar(t('track:page.history.activities.date-range-extended.hint.info'), { variant: 'info' });
      });
    }
  }, [hasExpandedDateRange]);

  const loadingJourney = Object.entries(mapLoading).find(([id]) => id === data.id)?.[1] ?? false;

  if (thingEventSnapShotsStatus === 'pending' || loadingJourney) {
    return (
      <Stack direction="row" spacing={2} alignItems="center" pl={1}>
        <CircularProgressIndicator
          color={color}
          isLoadingOthers={loadingJourney}
          loadingHint={loadingHint}
          retrievedCount={thingEventSnapShotsRetrieved}
          totalCount={thingEventSnapShotsTotalCount}
        />
        <Stack direction="column">
          {renderAccordionTitle()}
        </Stack>
      </Stack>
    );
  }

  return <>
    <StyledAccordion
      data-testid={props['data-testid']}
      expanded={collapsible && instanceExpanded}
      onChange={() => {
        handleExpand(data.entityId, data.id);
      }}
    >
      <StyledAccordionSummary
        expandIcon={collapsible ? <Icon icon="ChevronDown" data-testid="expand-icon" /> : <></>}
      >
        <Stack direction="row" spacing={1} alignItems="center" pl={collapsible ? 1 : 0}>
          <Icon data-testid="map-pin-icon" icon="MapPin" style={{ color, fontSize: '2rem' }} />
          <Stack direction="column">
            {renderAccordionTitle()}
          </Stack>
        </Stack>
        <Stack alignItems="center" direction="row" spacing={1}>
          {!smallScreen && isJourneyVisible &&
            <Tooltip title={t('common:common.labels.zoom-to-journey')} arrow>
              <StyledIconButton data-testid="zoom-to-journey-action-button" onClick={handleZoomTo}>
                <Icon data-testid="cross-hair-icon" icon="CrossHair" />
              </StyledIconButton>
            </Tooltip>
          }
          <StyledIconButton data-testid="eye-action-button" onClick={handleVisibility}>
            {isJourneyVisible ? <Icon data-testid="eye-icon" icon="Eye" /> : <Icon data-testid="eye-off-icon" icon="EyeOff" />}
          </StyledIconButton>
          <StyledIconButton data-testid="menu-action-button" onClick={handleClickActions}>
            <Icon data-testid="icon-dot-menu-vertical" icon="DotMenuVertical" />
          </StyledIconButton>
        </Stack>
      </StyledAccordionSummary>

      <Box sx={{ m: 0, p: 0, position: 'relative' }}>
        {collapsible ? <AccordionDetails>
          {flagShowLocationSwitch && <StyledFormControlLabel
            control={<Switch
              data-testid="show-locations-switch-input"
              disabled={locationState.isLoading || !hasLocationUpdate.current}
              checked={locationState.showLocationSwitchEnabled}
              color="primary"
              onChange={handleLocationToggle}
              inputProps={{ 'aria-busy': locationState.isLoading }}
            />}
            label={!hasLocationUpdate.current && hasNotableEvent.current
              ? t('common:common.labels.all-available-location-data-shown')
              : t('common:common.labels.show-location-updates')
            }
            isLoading={locationState.isLoading}
          />}
          {locationState.isLoading &&
            <Box sx={{ position: 'absolute', ...centered, width: 1, top: 36, height: 'auto' }}>
              <Typography color="text.secondary" fontWeight="bold" fontStyle="italic" >{loadingHint}</Typography>
            </Box>
          }
          {renderAccordionDetails}
        </AccordionDetails> : null}
      </Box>
      <EntityCardActions actions={journeyActions} anchorEl={anchorEl} open={actionsOpen} handleClose={handleCloseActions} />
    </StyledAccordion>

    <Collapse in={isEditingDate} unmountOnExit>
      <DateTimeRangePickerProvider>
        <InlineDateTimeRangePicker
          data-testid="datetime-picker"
          closeDateTimePicker={() => toggleEditingDate(data.id, data.entityId)}
          currentEndDateTime={endDateTime}
          currentStartDateTime={startDateTime}
          hideSeconds
          maxDateRange={maxDateRange}
          maxRangeUnit={HISTORY_DEFAULT_DATE_RANGE_UNIT}
          onDateRangeChanged={handleDateRangeChange}
        />
      </DateTimeRangePickerProvider>
    </Collapse>

    {selectedFaultItem && (
      <JourneyFaultsDialog
        key={selectedFaultItem.event.eventId}
        item={selectedFaultItem}
        isOpen={!!selectedFaultItem}
        onClose={handleCloseAnyDialog}
      />
    )}

    {summarizedEventsDialogOpen && (
      !smallScreen ? (
        <Dialog
          key={data.entityId}
          disableEnforceFocus
          closeAfterTransition={false}
          open={summarizedEventsDialogOpen}
          onClose={handleCloseAnyDialog}
          maxWidth='lg'
          fullWidth
        >
          <Box width={theme.breakpoints.values.md}>
            <ClusterTabularData
              data={summarizedEventsForDialog}
              onEventSelect={({ row }: { row: { id: string } }) => {
                const index = summarizedEventsForDialog.findIndex((e) => e.eventId === row.id);
                if (index !== -1) {
                  setEvents(summarizedEventsForDialog);
                  setEventIndex(index);
                  setOpen(true);
                }
              }}
            />
          </Box>
        </Dialog>
      ) : (
        <StyledSwipeableDrawer anchor='bottom' disableSwipeToOpen open={summarizedEventsDialogOpen} onOpen={() => { }} onClose={handleCloseAnyDialog}>
          <Box position='absolute' top='0' right='0' m={1} zIndex={500}>
            <IconButton onClick={handleCloseAnyDialog}><Icon icon="ChevronDown" /></IconButton>
          </Box>
          <Box height='98vh' width='100%'>
            <ClusterTabularData
              data={summarizedEventsForDialog}
              onEventSelect={({ row }: { row: { id: string } }) => {
                const index = summarizedEventsForDialog.findIndex((e) => e.eventId === row.id);
                onItemClick(index);
              }}
            />
          </Box>
        </StyledSwipeableDrawer>
      )
    )}
  </>;
};

interface UseDisplaySnackbarAfterTimeoutOptions {
  enabled?: boolean;
  isPending: boolean;
  message: string;
  timeoutMs: number;
}

const useDisplaySnackbarAfterTimeout = ({ enabled, isPending, message, timeoutMs }: UseDisplaySnackbarAfterTimeoutOptions): void => {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (enabled && timeoutMs && isPending) {
      const timeout = setTimeout(() => {
        enqueueSnackbar(message, { preventDuplicate: true });
      }, timeoutMs);

      return (() => {
        clearTimeout(timeout);
      });
    }
  }, [enqueueSnackbar, enabled, isPending, message, timeoutMs]);
};
